import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import PowerIconButton from '@erikdakoda/ui/components/PowerIconButton';
import type { AiMessageForUi } from '@erikdakoda/convo/types';
import { extractArtifactInputsFromMessages } from '@erikdakoda/artifact/shared/extractArtifactInputsFromMessages';
import { useArtifactStore } from '../hooks/useArtifactStore';
import EmbeddedArtifact from './EmbeddedArtifact';
import ArtifactCopyDownloadButtons from './ArtifactCopyDownloadButtons';
import { pushArtifactToPanel } from '../shared/pushArtifactToPanel';
import { syncArtifactStoreFromMessageInputs } from '../shared/syncArtifactStoreFromMessageInputs';
import { getArtifactType } from '../shared/artifactRegistry';

type ArtifactPanelProps = {
  /** MUI spacing units of right inset to clear CraftPage drawer tab buttons. */
  sideInset?: number;
  /** Convo messages used to refresh artifact content from the latest tool input. */
  messages?: readonly AiMessageForUi[];
};

type ArtifactPanelBodyProps = {
  type: string;
  content: string;
  title: string;
  contentReloadKey: number;
};

/**
 * Workspace panel for viewing the active artifact. Fullscreen is handled by `ArtifactFullscreenDialog`.
 */
export default function ArtifactPanel({ sideInset, messages = [] }: ArtifactPanelProps) {
  'use memo';

  const artifacts = useArtifactStore((state) => state.artifacts);
  const activeIdentifier = useArtifactStore((state) => state.activeIdentifier);
  const panelOpen = useArtifactStore((state) => state.panelOpen);
  const closePanel = useArtifactStore((state) => state.closePanel);
  const setFullscreen = useArtifactStore((state) => state.setFullscreen);
  const bumpContentRevision = useArtifactStore((state) => state.bumpContentRevision);
  const contentRevision = useArtifactStore((state) =>
    activeIdentifier ? (state.contentRevision[activeIdentifier] ?? 0) : 0,
  );

  const artifact = activeIdentifier ? artifacts[activeIdentifier] : null;

  useEffect(() => {
    if (panelOpen && !artifact) {
      closePanel();
    }
  }, [panelOpen, artifact, closePanel]);

  if (!artifact) {
    return null;
  }

  const handleRefresh = (): void => {
    if (!activeIdentifier) {
      return;
    }

    const latestInput = extractArtifactInputsFromMessages(messages).find(
      (input) => input.identifier === activeIdentifier,
    );
    if (latestInput) {
      syncArtifactStoreFromMessageInputs([latestInput]);
    }

    bumpContentRevision(activeIdentifier);
  };

  const renderArtifactBody = (): React.ReactElement => (
    <ArtifactPanelBody
      type={artifact.type}
      content={artifact.content}
      title={artifact.title}
      contentReloadKey={contentRevision}
    />
  );

  return (
    <Box
      className="artifact-panel"
      sx={(theme) => ({
        width: sideInset ? `calc(100% - ${theme.spacing(sideInset)})` : '100%',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        display: 'grid',
        gridTemplateRows: 'min-content 1fr',
        overflow: 'hidden',
      })}
    >
      <ArtifactPanelHeader
        type={artifact.type}
        title={artifact.title}
        content={artifact.content}
        onMaximize={() => setFullscreen(true)}
        onRefresh={handleRefresh}
        onClose={closePanel}
      />
      {renderArtifactBody()}
    </Box>
  );
}

/**
 * Panel body — each artifact type owns scrolling (ScrollBox or iframe).
 */
function ArtifactPanelBody({
  type,
  content,
  title,
  contentReloadKey,
}: ArtifactPanelBodyProps): React.ReactElement {
  'use memo';

  return (
    <Box
      className="artifact-panel-body"
      sx={{ height: '100%', minHeight: 0, minWidth: 0, overflow: 'hidden' }}
    >
      <EmbeddedArtifact
        type={type}
        content={content}
        title={title}
        onOpenArtifact={pushArtifactToPanel}
        fillPanel
        contentReloadKey={contentReloadKey}
      />
    </Box>
  );
}

type ArtifactPanelHeaderProps = {
  type: string;
  title: string;
  content: string;
  onMaximize: () => void;
  onRefresh: () => void;
  onClose: () => void;
};

function ArtifactPanelHeader({
  type,
  title,
  content,
  onMaximize,
  onRefresh,
  onClose,
}: ArtifactPanelHeaderProps): React.ReactElement {
  'use memo';

  const { t } = useTranslation();
  const definition = getArtifactType(type);
  const typeName = definition?.name ?? type;
  const typeIcon = definition?.icon ?? 'file';

  return (
    <Box
      className="artifact-panel-header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 3,
        gap: 4,
        minWidth: 0,
        overflow: 'hidden',
        height: '3rem',
        backgroundColor: 'background.header',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Tooltip title={typeName} enterDelay={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FaIconUvilo size="xl" icon={typeIcon} prefix="fat" />
        </Box>
      </Tooltip>

      <Typography variant="condensed5" sx={{ flex: 1, minWidth: 0, lineHeight: 1.2 }} noWrap>
        {title}
      </Typography>
      <Box
        className="artifact-panel-header-actions"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <PowerIconButton
          size="small"
          padding={2}
          sx={{ my: -2 }}
          icon="maximize"
          title={t('artifact-ui:artifactOpenFullscreen')}
          onClick={onMaximize}
        />
        <PowerIconButton
          size="small"
          padding={2}
          sx={{ my: -2 }}
          icon="arrow-rotate-right"
          title={t('artifact-ui:artifactRefresh')}
          onClick={onRefresh}
        />
        <ArtifactCopyDownloadButtons type={type} content={content} title={title} />
      </Box>
      <PowerIconButton
        size="small"
        padding={2}
        sx={{ my: -2 }}
        icon="xmark"
        title={t('artifact-ui:artifactClosePanel')}
        onClick={onClose}
      />
    </Box>
  );
}
